import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import { toast } from "react-toastify";
import { formatToZMW, rootUrl } from "../../../helpers";
import { useAuth } from "../../../context/auth-context";


const Cash = () => {
  const [cashData, setCashData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState("");
  const [amount, setAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCashData();
  }, []);

  const auth = useAuth();

  const fetchCashData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(rootUrl(`/getCash/${auth.user.shop}`)); // Adjust the API URL
      setCashData(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch cash data");
    }
    setLoading(false);
  };

  const handleTransfer = async () => {
    if (!selectedShop || !amount) {
      toast.error("Please select a shop and enter an amount");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(rootUrl(`/moveCash`), {
        shop: auth.user.shop,
        stop: selectedShop,
        amount: parseFloat(amount),
      });

      if (response.data.status == 200) {
        toast.success("Transfer successful!");
        setIsModalOpen(false);
        fetchCashData();
      } else {
        toast.error("Transfer failed, try again.");
      }
    } catch (error) {
      toast.error("Error processing transfer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    { name: "Ref Type", selector: (row) => row.refType, sortable: true },
    { name: "Amount", selector: (row) => row.amount, sortable: true },
    { name: "Type", selector: (row) => (row.type == 1 ? "Income" : "Expense"), sortable: true },
    { name: "Comment", selector: (row) => row.comment },
    { name: "Date", selector: (row) => new Date(row.date).toLocaleString(), sortable: true },
  ];

  const totalIncome = cashData.filter((d) => d.type == 1).reduce((acc, curr) => acc + curr.amount, 0);
  const totalExpense = cashData.filter((d) => d.type == 2).reduce((acc, curr) => acc + curr.amount, 0);
  const balance = totalIncome - totalExpense;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Cash Transactions</h1>
      <DataTable
        columns={columns}
        data={cashData}
        progressPending={loading}
        pagination
        highlightOnHover
      />

      {/* Totals Section */}
      <div className="mt-4 p-4 border-t">
        <p className="text-lg font-bold">Total Income: <span className="text-green-600">${formatToZMW(totalIncome)}</span></p>
        <p className="text-lg font-bold">Total Expense: <span className="text-red-600">${formatToZMW(totalExpense)}</span></p>
        <p className="text-lg font-bold">Balance: <span className="text-blue-600">${formatToZMW(balance)}</span></p>
      </div>

      {/* Transfer Button */}
      <button
        className="bg-blue-500 text-white px-4 py-2 rounded mt-4"
        onClick={() => setIsModalOpen(true)}
      >
        Transfer
      </button>

      {/* Transfer Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h2 className="text-xl font-bold mb-4">Transfer Funds</h2>

            {/* Shop Selection */}
            <label className="block mb-2 font-semibold">Select Shop:</label>
            <select
              className="w-full p-2 border rounded mb-4"
              value={selectedShop}
              onChange={(e) => setSelectedShop(e.target.value)}
            >
              <option value="">Select Shop</option>
              {[...Array(7)].map((_, index) => (
                <option key={index + 1} value={index + 1}>
                  Shop {index + 1}
                </option>
              ))}
            </select>

            {/* Amount Input */}
            <label className="block mb-2 font-semibold">Amount:</label>
            <input
              type="number"
              className="w-full p-2 border rounded mb-4"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />

            {/* Transfer Buttons */}
            <div className="flex justify-end space-x-2">
              <button
                className="bg-gray-500 text-white px-4 py-2 rounded"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className={`bg-green-500 text-white px-4 py-2 rounded ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={handleTransfer}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cash;
